"use client";

import React from "react";
import { ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function getStatusBadge(rawStatus?: string) {
  if (!rawStatus) return null;
  const status = rawStatus.trim().toLowerCase();
  if (status === "in stock" || status === "available now") {
    return {
      label: "Available Now",
      className: "bg-emerald-500/10 text-emerald-600 border border-emerald-500/20 dark:text-emerald-400"
    };
  }
  if (status === "target price" || status === "high demand") {
    return {
      label: "High Demand",
      className: "bg-amber-500/10 text-amber-600 border border-amber-500/20 dark:text-amber-400"
    };
  }
  if (status === "immediate allocation" || status === "urgent deal") {
    return {
      label: "Urgent Deal",
      className: "bg-rose-500/10 text-rose-600 border border-rose-500/20 dark:text-rose-400"
    };
  }
  return {
    label: rawStatus,
    className: "bg-muted text-foreground border border-border"
  };
}
function buildWhatsAppUrl(category: string, volume: string, region: string) {
  const message = `Hi, I'm interested in this deal:\n${category}\n${volume}\n${region}\nPlease share details.`;
  return `https://wa.me/971562568787?text=${encodeURIComponent(message)}`;
}
function TradingOpportunitiesConsumer() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div className="space-y-4">
        <div className="hidden md:block rounded-xl border border-border bg-card p-6">
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => <div key={i} data-index={i} className="h-12 w-full animate-pulse rounded-md bg-muted/40" />)}
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 md:hidden">
          {[...Array(4)].map((_, i) => <div key={i} data-index={i} className="h-40 animate-pulse rounded-lg border border-border bg-card" />)}
        </div>
      </div>;
  }
  if (error) {
    return <div className="rounded-xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">
        <p className="mb-3">Unable to load current trading opportunities.</p>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          Retry
        </Button>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="rounded-xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">
        <p className="mb-0">No active trading opportunities match the current criteria.</p>
      </div>;
  }
  return <>
      {/* Desktop Table (768px and up) */}
      <div className="hidden md:block rounded-xl border border-[#e0e0e0] bg-[#ffffff] shadow-sm overflow-hidden">
        <Table>
          <TableHeader className="bg-muted/40">
            <TableRow className="hover:bg-transparent border-border">
              <TableHead className="w-[180px] py-3.5 px-6 font-semibold text-foreground text-xs uppercase tracking-wider">
                Category
              </TableHead>
              <TableHead className="py-3.5 px-6 font-semibold text-foreground text-xs uppercase tracking-wider">
                Destination
              </TableHead>
              <TableHead className="w-[160px] py-3.5 px-6 font-semibold text-foreground text-xs uppercase tracking-wider">
                Quantity
              </TableHead>
              <TableHead className="w-[150px] py-3.5 px-6 font-semibold text-foreground text-xs uppercase tracking-wider">
                Status
              </TableHead>
              <TableHead className="w-[130px] py-3.5 px-6 text-right font-semibold text-foreground text-xs uppercase tracking-wider">
                Action
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {posts.map((post: any, i: number) => {
            const category = post.acf?.category || post.customFields?.category || "General";
            const region = post.acf?.region || post.customFields?.region || "Global";
            const volume = post.acf?.volume || post.customFields?.volume || "Contact for Volume";
            const rawStatus = post.acf?.status || post.customFields?.status;
            const statusBadge = getStatusBadge(rawStatus);
            const waUrl = buildWhatsAppUrl(category, volume, region);
            return <TableRow key={post.id} data-index={i} className="border-border hover:bg-muted/20 transition-colors">
                  <TableCell className="px-6 py-4">
                    <span className="inline-block rounded-md bg-muted px-2.5 py-1 text-xs font-medium text-foreground">
                      {category}
                    </span>
                  </TableCell>
                  <TableCell className="px-6 py-4">
                    <div className="font-medium text-foreground text-sm">
                      {region}
                    </div>
                  </TableCell>
                  <TableCell className="px-6 py-4">
                    <span className="font-semibold text-foreground text-sm">
                      {volume}
                    </span>
                  </TableCell>
                  <TableCell className="px-6 py-4">
                    {statusBadge ? <span className={`inline-flex items-center rounded-md px-2.5 py-0.5 text-xs font-medium ${statusBadge.className}`}>
                        {statusBadge.label}
                      </span> : <span className="text-xs text-muted-foreground">-</span>}
                  </TableCell>
                  <TableCell className="px-6 py-4 text-right">
                    <Button asChild size="sm" variant="ghost" className="h-8 px-2 text-primary hover:text-primary">
                      <Link to={waUrl} newTab aria-label={`Enquire about ${category} on WhatsApp`}>
                        <span>Enquire Now</span>
                        <ArrowUpRight className="ml-1 size-3.5" />
                      </Link>
                    </Button>
                  </TableCell>
                </TableRow>;
          })}
          </TableBody>
        </Table>
      </div>

      {/* Mobile Stacked Cards (below 768px) */}
      <div className="grid grid-cols-1 gap-4 md:hidden">
        {posts.map((post: any, i: number) => {
        const category = post.acf?.category || post.customFields?.category || "General";
        const region = post.acf?.region || post.customFields?.region || "Global";
        const volume = post.acf?.volume || post.customFields?.volume || "Contact for Volume";
        const rawStatus = post.acf?.status || post.customFields?.status;
        const statusBadge = getStatusBadge(rawStatus);
        const waUrl = buildWhatsAppUrl(category, volume, region);
        return <div key={post.id} data-index={i}>
              <Card className="border border-border bg-card shadow-xs rounded-lg py-4">
                <CardHeader className="px-4 pb-2 pt-0 space-y-2">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="inline-block rounded bg-muted px-2 py-0.5 text-2xs font-medium text-foreground uppercase tracking-wider">
                        {category}
                      </span>
                      {statusBadge && <span className={`inline-flex items-center rounded px-2 py-0.5 text-2xs font-medium ${statusBadge.className}`}>
                          {statusBadge.label}
                        </span>}
                    </div>
                    <span className="text-xs font-semibold text-foreground">
                      {volume}
                    </span>
                  </div>
                  <CardTitle className="text-base font-semibold text-foreground pt-1">
                    {region}
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-4 pt-3 border-t border-border/60">
                  <Button asChild size="sm" variant="outline" className="w-full justify-center">
                    <Link to={waUrl} newTab aria-label={`Enquire about ${category} on WhatsApp`}>
                      <span>Enquire Now</span>
                      <ArrowUpRight className="ml-1 size-3.5" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            </div>;
      })}
      </div>
    </>;
}
export default function TradingOpportunities() {
  const wp_query_params = {
    post_type: "trading_opportunity",
    per_page: 8,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="tradingopportunities" data-nav="light" className="relative bg-[#f5f7fa] py-16 md:py-24 border-b border-border text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Intro Block */}
        <div className="max-w-3xl mb-10 md:mb-12 space-y-4">
          <h2 className="text-4xl sm:text-5xl font-bold tracking-tight text-foreground">
            Access daily trade requests
            <span className="text-[var(--color-accent)]">.</span>
          </h2>
          <p className="text-base sm:text-lg text-[#555555] leading-relaxed mb-6">
            Verified wholesale allocations and immediate buyer requests updated daily for global partners.
          </p>
          <div className="flex flex-wrap items-center gap-3 pt-1">
            <Button asChild variant="default" className="shadow-sm">
              <Link to="#">View Daily Requests</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="#">Become a Verified Buyer</Link>
            </Button>
          </div>
        </div>

        {/* Action Row Below Intro */}
        <div className="flex flex-wrap items-center justify-between gap-4 pb-6 mb-8 border-b border-border/60">
          <p className="text-sm font-medium text-muted-foreground mb-0">
            Live allocations and wholesale parcel inventory
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <Button asChild variant="default" size="sm" className="shadow-sm">
              <Link to="#">
                <span>Access full inventory</span>
                <ArrowUpRight className="ml-1 size-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to="#">Request custom quote</Link>
            </Button>
          </div>
        </div>

        {/* Dynamic CPT Trading Opportunities Consumer */}
        <PostsProvider wp_query={{
        post_type: "trading_opportunity",
        per_page: 8,
        orderby: "date",
        order: "desc",
        status: ["publish"]
      }}>
          <TradingOpportunitiesConsumer />
        </PostsProvider>

        {/* Bottom Verification Note */}
        <div className="mt-8 flex flex-wrap items-center justify-end gap-4 rounded-lg bg-muted/30 border border-border p-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-4">
            <span>Minimum order quantities apply per deal line.</span>
            <Link to="#" className="text-primary font-medium hover:underline">
              Have inventory to sell?
            </Link>
          </div>
        </div>
      </div>
    </section>;
}